<template>
  <section class="bg-mm-mmmorelight">
    <div class="w-full grid lg:grid-cols-[280px_1fr] gap-0 bg-mm-mmdark">
      <!-- LEFT MENU -->
      <aside class="bg-[#EEDCC5] p-6 lg:p-8">
        <div class="mb-6 lg:block hidden">
          <h2 class="text-[28px] font-black italic uppercase text-[#111]">{{ t('productManage.dashboard.menu.myAccount') }}</h2>
        </div>

        <!-- Mobile Dropdown Toggle -->
        <div class="lg:hidden mb-4">
          <button
            type="button"
            @click="showMobileMenu = !showMobileMenu"
            class="w-full flex items-center justify-between px-4 py-3 bg-white border border-black italic uppercase font-black text-[18px]"
          >
            <span>{{ currentLabel }}</span>
            <svg
              class="w-5 h-5 transition-transform duration-200"
              :class="{ 'rotate-180': showMobileMenu }"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>

        <nav
          class="flex-col gap-2 text-[16px] lg:flex"
          :class="showMobileMenu ? 'flex' : 'hidden'"
        >
          <button type="button" @click="select('overview')"
                  :class="btnClass('overview')">{{ t('productManage.dashboard.menu.overview') }}</button>
          <button type="button" @click="select('profile')"
                  :class="btnClass('profile')">{{ t('productManage.dashboard.menu.myDetails') }}</button>
          <button type="button" @click="select('password')"
                  :class="btnClass('password')">{{ t('productManage.dashboard.menu.changePassword') }}</button>
          <button v-if="isBedrijf || isSchool" type="button" @click="select('claims')"
                  :class="btnClass('claims')">{{ t('productManage.dashboard.menu.myClaims') }}</button>
          <button v-if="isBedrijf || isSchool" type="button" @click="select('manage')"
                  :class="btnClass('manage')">{{ t('productManage.dashboard.menu.myMaterials') }}</button>
          <button v-if="isBedrijf || isSchool" type="button" @click="select('create')"
                  :class="btnClass('create')">{{ t('productManage.dashboard.menu.addMaterial') }}</button>
          <!-- Coordinator menu -->
          <template v-if="isCoordinator">
            <div class="mt-4 mb-1 text-[12px] uppercase italic text-[#6b6256]">{{ t('productManage.dashboard.menu.regionManagement') }}</div>
            <button type="button" @click="select('region-materials')"
                    :class="btnClass('region-materials')">{{ t('productManage.dashboard.menu.regionMaterials') }}</button>
            <button type="button" @click="select('region-claims')"
                    :class="btnClass('region-claims')">{{ t('productManage.dashboard.menu.regionClaims') }}</button>
            <button type="button" @click="select('region-companies')"
                    :class="btnClass('region-companies')">{{ t('productManage.dashboard.menu.regionCompanies') }}</button>
            <button type="button" @click="select('region-schools')"
                    :class="btnClass('region-schools')">{{ t('productManage.dashboard.menu.regionSchools') }}</button>
          </template>
          <!-- Rol-wissel knop: alleen zichtbaar als gebruiker meerdere rollen heeft -->
          <template v-if="hasSwitchableRoles">
            <div class="mt-4 mb-1 text-[12px] uppercase italic text-[#6b6256]">{{ tOr('roleSwitcher.label', 'Rol wisselen') }}</div>
            <button
              v-for="role in switchableRoles"
              :key="role"
              type="button"
              @click="handleSwitchRole(role)"
              :disabled="roleSwitchLoading"
              :class="[
                'text-left px-3 py-2 border italic uppercase font-semibold flex items-center gap-2',
                activeRole === role
                  ? 'bg-black text-white border-black'
                  : 'bg-white text-black border-[#b8b0a3] hover:bg-[#f6efe4]',
                roleSwitchLoading ? 'opacity-60 cursor-not-allowed' : ''
              ]"
            >
              <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
              </svg>
              <span>{{ roleLabel(role) }}</span>
              <span v-if="activeRole === role" class="ml-auto text-[10px] font-black">✓</span>
            </button>
            <div v-if="roleSwitchError" class="text-[12px] text-red-600 px-1">{{ roleSwitchError }}</div>
          </template>
          <button type="button" @click="handleLogout"
                  class="text-left px-3 py-2 border border-black bg-white hover:bg-[#f6efe4] italic uppercase font-semibold flex items-center gap-2 mt-2">
            <!-- Logout icon (zelfde als header-stijl: pijltje naar buiten) -->
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span>{{ t('productManage.dashboard.menu.logout') }}</span>
          </button>
        </nav>
      </aside>

      <!-- RIGHT CONTENT -->
      <div class="p-6 lg:p-8 min-w-0">
        <div v-if="loading" class="text-[15px] text-[#666]">{{ t('messages.loading') }}</div>

        <template v-else>
          <!-- OVERVIEW -->
          <div v-if="view==='overview'" class="space-y-6 w-full">
            <component :is="components.MmDashboard" :user="user" :t="t" @select="select" />
          </div>

          <!-- PROFILE -->
          <component v-else-if="view==='profile'" :is="components.MmProfile" :embedded="true" :initial-user="user" :t="t" />

          <!-- CHANGE PASSWORD -->
          <component v-else-if="view==='password'" :is="components.MmChangePassword" :embedded="true" :t="t" />

          <!-- MANAGE MATERIALS (bedrijf of school) -->
          <component v-else-if="view==='manage' && (isBedrijf || isSchool)" :is="components.MmProductManage" :embedded="true" :initial-material-id="mid" :t="t" />

          <!-- MIJN CLAIMS (school of bedrijf) -->
          <component v-else-if="view==='claims' && (isSchool || isBedrijf)" :is="components.MmMyClaims" :embedded="true" :t="t" />

          <!-- CREATE MATERIAL (bedrijf of school) -->
          <component v-else-if="view==='create' && (isBedrijf || isSchool)" :is="components.MmProductCreate" :embedded="true" :t="t" />

          <!-- Coordinator: Regio materialen -->
          <component v-else-if="view==='region-materials' && isCoordinator" :is="components.MmRegionMaterials" :embedded="true" :t="t" />
          <!-- Coordinator: Regio claims (voorheen deals) -->
          <component v-else-if="view==='region-claims' && isCoordinator" :is="components.MmRegionClaims" :uid="uid" :embedded="true" :t="t" />
          <!-- Coordinator: Regio bedrijven -->
          <component v-else-if="view==='region-companies' && isCoordinator" :is="components.MmRegionUsers" role="bedrijf" :uid="uid" :embedded="true" :t="t" />
          <!-- Coordinator: Regio scholen -->
          <component v-else-if="view==='region-schools' && isCoordinator" :is="components.MmRegionUsers" role="school" :uid="uid" :embedded="true" :t="t" />
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import { defineAsyncComponent } from 'vue'
import { t } from '../i18n'

// Lazy-load alle subcomponenten om de initiële laadtijd van de accountpagina te verbeteren
const AsyncMmProfile = defineAsyncComponent(() => import('./MmProfile.vue'))
const AsyncMmChangePassword = defineAsyncComponent(() => import('./MmChangePassword.vue'))
const AsyncMmProductCreate = defineAsyncComponent(() => import('./MmProductCreate.vue'))
const AsyncMmProductManage = defineAsyncComponent(() => import('./MmProductManage.vue'))
const AsyncMmMyClaims = defineAsyncComponent(() => import('./MmMyClaims.vue'))
const AsyncMmDashboard = defineAsyncComponent(() => import('./MmDashboard.vue'))
const AsyncMmRegionMaterials = defineAsyncComponent(() => import('./MmRegionMaterials.vue'))
const AsyncMmRegionClaims = defineAsyncComponent(() => import('./MmRegionClaims.vue'))
const AsyncMmRegionUsers = defineAsyncComponent(() => import('./MmRegionUsers.vue'))

export default {
  name: 'MmAccount',
components: {},
  props: {
    // Maakt het mogelijk om direct een subview te openen, zoals 'profile' of 'password'
    initialView: { type: String, default: '' }
  },
  data(){
    return {
      loading: true,
      user: null,
      view: 'overview',
      uid: null,
      mid: null,
      api: null,
      showMobileMenu: false,
      roleSwitchLoading: false,
      roleSwitchError: '',
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.view = this.viewFromPath()
      }
    }
  },
  computed: {
    currentLabel(){
      const labels = {
        'profile': this.t('productManage.dashboard.menu.myDetails'),
        'password': this.t('productManage.dashboard.menu.changePassword'),
        'claims': this.t('productManage.dashboard.menu.myClaims'),
        'manage': this.t('productManage.dashboard.menu.myMaterials'),
        'create': this.t('productManage.dashboard.menu.addMaterial'),
        'region-materials': this.t('productManage.dashboard.menu.regionMaterials'),
        'region-claims': this.t('productManage.dashboard.menu.regionClaims'),
        'region-companies': this.t('productManage.dashboard.menu.regionCompanies'),
        'region-schools': this.t('productManage.dashboard.menu.regionSchools'),
        'overview': this.t('productManage.dashboard.menu.overview')
      }
      return labels[this.view] || 'Menu'
    },
    components(){
      return {
        MmProfile: AsyncMmProfile,
        MmChangePassword: AsyncMmChangePassword,
        MmProductCreate: AsyncMmProductCreate,
        MmProductManage: AsyncMmProductManage,
        MmMyClaims: AsyncMmMyClaims,
        MmDashboard: AsyncMmDashboard,
        MmRegionMaterials: AsyncMmRegionMaterials,
        MmRegionClaims: AsyncMmRegionClaims,
        MmRegionUsers: AsyncMmRegionUsers,
      }
    },
    isBedrijf(){
      const active = this.activeRole.toLowerCase()
      if (active) return active === 'bedrijf'
      return ((this.user?.roleType)||'').toLowerCase()==='bedrijf'
    },
    isSchool(){
      const active = this.activeRole.toLowerCase()
      if (active) return active === 'school'
      return ((this.user?.roleType)||'').toLowerCase()==='school'
    },
    isCoordinator(){
      const active = this.activeRole.toLowerCase()
      if (active) return active === 'coordinator'
      const rt = ((this.user?.roleType)||'').toLowerCase()
      return !!(this.user?.isCoordinator) || rt==='coordinator'
    },
    switchableRoles(){
      return (this.user?.switchableRoles) || []
    },
    hasSwitchableRoles(){
      return this.switchableRoles.length >= 2
    },
    activeRole(){
      return (this.user?.activeRole) || (this.user?.roleType) || ''
    }
  },
  methods: {
    t,
    tOr(key, fallback){
      const v = t(key)
      return (v && v !== key) ? v : fallback
    },
    roleLabel(role) {
      const map = { coordinator: 'Coördinator', school: 'School', bedrijf: 'Bedrijf' }
      return map[role] || role
    },
    async handleSwitchRole(role) {
      if (role === this.activeRole) return
      this.roleSwitchLoading = true
      this.roleSwitchError = ''
      try {
        const res = await (this.api || new WpApiClient()).switchRole({
          role,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined
        })
        if (res && res.ok) {
          // Redirect naar het dashboard/overzicht zodat de nieuwe rol direct zichtbaar is
          window.location.href = '/account'
        } else {
          this.roleSwitchError = (res && res.error) || this.tOr('messages.genericError', 'Er ging iets mis.')
        }
      } catch(e) {
        this.roleSwitchError = e?.message || this.tOr('messages.genericError', 'Er ging iets mis.')
      }
      this.roleSwitchLoading = false
    },
    cleanPath(p){
      try{
        if (!p) return ''
        // verwijder trailing slashes, behoud root '/'
        const noQuery = p.split('?')[0].split('#')[0]
        const cleaned = noQuery.replace(/\/+$/,'')
        return cleaned === '' ? '/' : cleaned
      } catch(_) { return p || '' }
    },
    viewFromPath(){
      try{
        const raw = (window && window.location && window.location.pathname) ? window.location.pathname : ''
        const path = this.cleanPath(raw)
        const params = new URLSearchParams(window.location.search)
        this.uid = params.get('uid')
        const claimId = params.get('claim_id')
        if (claimId) this.uid = claimId
        this.mid = params.get('mid')
        if (path === '/account/account-wijzigen') return 'profile'
        if (path === '/account/wachtwoord-wijzigen') return 'password'
        if (path === '/account/mijn-claims' || path === '/account/mijn-deals') return 'claims'
        if (path === '/account/mijn-materialen') return 'manage'
        if (path === '/account/materialen-toevoegen') return 'create'
        if (path === '/account/regio-materialen') return 'region-materials'
        if (path === '/account/regio-claims' || path === '/account/regio-deals') return 'region-claims'
        if (path === '/account/regio-bedrijven') return 'region-companies'
        if (path === '/account/regio-scholen') return 'region-schools'
        return 'overview'
      } catch(_) { return 'overview' }
    },
    btnClass(key){
      const active = this.view === key
      return [
        'text-left px-3 py-2 border italic uppercase font-semibold',
        active ? 'bg-black text-white border-black' : 'bg-white text-black border-[#b8b0a3] hover:bg-[#f6efe4]'
      ]
    },
    select(key, id = null){
      this.view = key
      if (key === 'region-materials') {
        this.mid = id
        this.uid = null
      } else if (key === 'region-claims') {
        this.uid = id
        this.mid = null
      } else if (key === 'manage') {
        this.mid = id
        this.uid = null
      } else {
        this.uid = id
        this.mid = null
      }
      this.showMobileMenu = false
      // Optional: reflect URL when moving to profile editor
      try {
        const raw = (window && window.location && window.location.pathname) ? window.location.pathname : ''
        const path = this.cleanPath(raw)
        let query = ''
        if (key === 'region-claims' && this.uid) query = `?claim_id=${this.uid}`
        else if (this.uid) query = `?uid=${this.uid}`
        
        if (this.mid) query = `?mid=${this.mid}`

        if (key === 'profile') {
          window.history.replaceState(null, '', '/account/account-wijzigen' + query)
        } else if (key === 'password') {
          window.history.replaceState(null, '', '/account/wachtwoord-wijzigen' + query)
        } else if (key === 'claims') {
          window.history.replaceState(null, '', '/account/mijn-claims' + query)
        } else if (key === 'region-claims') {
          window.history.replaceState(null, '', '/account/regio-claims' + query)
        } else if (key === 'manage') {
          // Mijn materialen
          window.history.replaceState(null, '', '/account/mijn-materialen' + query)
        } else if (key === 'create') {
          // Materiaal toevoegen
          window.history.replaceState(null, '', '/account/materialen-toevoegen' + query)
        } else if (key === 'region-materials') {
          window.history.replaceState(null, '', '/account/regio-materialen' + query)
        } else if (key === 'region-deals') {
          window.history.replaceState(null, '', '/account/regio-deals' + query)
        } else if (key === 'region-companies') {
          window.history.replaceState(null, '', '/account/regio-bedrijven' + query)
        } else if (key === 'region-schools') {
          window.history.replaceState(null, '', '/account/regio-scholen' + query)
        } else {
          // overview or others → back to /account from willekeurig nested account pad
          if (
            path !== '/account' || query
          ) {
            window.history.replaceState(null, '', '/account' + query)
          }
        }
      } catch(_) {}
    },
    async load(){
      this.api = new WpApiClient()
      try{
        let res = await this.api.getCurrentUser({ nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined })
        
        // Automatische retry bij nonce error
        if (res && !res.ok && (res.error === 'invalid_nonce' || (res.error && res.error.includes('nonce')))) {
          const newNonce = await this.api.refreshNonce(false);
          if (newNonce) {
            res = await this.api.getCurrentUser({ nonce: newNonce });
          }
        }

        if (res && res.ok && res.user) {
          this.user = res.user
        } else {
          // Not logged in, go to login page
          window.location.href = '/inloggen'
          return
        }
      } catch(_){ window.location.href = '/inloggen'; return }
      this.loading = false
      // Na het laden van de user: behoud de subview van de URL, maar valideer coördinatorrechten
      const desired = this.viewFromPath()
      const coordViews = ['region-materials','region-deals','region-companies','region-schools']

      if (desired === 'overview' || !desired) {
        this.select('overview')
      } else if (coordViews.includes(desired) && !this.isCoordinator) {
        this.select('profile')
      } else {
        this.view = desired
      }
    },
    async handleLogout(){
      try { await (this.api || new WpApiClient()).logout({ nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined }) } catch(_){ }
      // Altijd naar de pretty route /inloggen i.p.v. wp-login.php
      window.location.href = '/inloggen'
    }
  },
    mounted(){
      // Stel beginweergave in op basis van prop of URL zonder rol-check; na load valideren we.
      if (this.initialView) {
        this.view = this.initialView
      } else {
        this.view = this.viewFromPath()
      }
      this.load()
    }
  }
</script>

<style scoped>
</style>
